import React, {useCallback, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {
    clearStrip,
    removePill,
    selectCriteriaStrip,
    selectCriteriaStripView,
} from 'src/components/criteria-strip/services/criteria-strip-slice';
import {clearSelection} from 'src/components/data-table-selection/services/data-table-selection-slice';
import {useEffectWithDeps} from 'src/hooks/useEffectWithDeps';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {isDeepEqual} from 'src/utils/isDeepEqual';

import {FlatButton} from '@pb/kit-component-button';
import {Icon, Icons} from '@pb/kit-component-icon';
import {Scrollable} from '@pb/kit-component-scrollable';
import {Pill, StripOfPills} from '@pb/kit-component-strip-of-pills';


import {ACCOUNT_STRIP_LABELS, USER_STRIP_LABELS} from './constants';

import {criteriaStripClearButton, criteriaStripGroup, criteriaStripPill} from './criteria-strip.module.styl';

import type {CriteriaValue} from 'src/components/criteria-strip/services/criteria-strip-slice';
import type {AccountSearchCriteria} from 'src/openapi/admin/models/AccountSearchCriteria';
import type {UserSearchCriteria} from 'src/openapi/admin/models/UserSearchCriteria';

type SearchCriteria = AccountSearchCriteria & UserSearchCriteria;

type Props = {
    activeTab: string,
    showClear?: boolean,
};

export const CriteriaStrip: React.FC<Props> = ({showClear, activeTab}) => {
    const dispatch = useDispatch();

    const stripData = useSelector(selectCriteriaStripView);
    const strip = useSelector(selectCriteriaStrip);

    const {searchCriteriaAccount, searchCriteriaUser} = useEventTracking();

    useEffectWithDeps((prev, current) => {
        if (!isDeepEqual({prev, current})) {
            dispatch(clearSelection('account-search-result'));
            dispatch(clearSelection('user-search-result'));
        }
    }, strip);

    const trackPillRemoving = useCallback((propName: keyof SearchCriteria) => {
        if (activeTab === 'accounts') {
            const accountStripLabel: string | undefined = ACCOUNT_STRIP_LABELS[
                propName as keyof typeof ACCOUNT_STRIP_LABELS
            ];
            searchCriteriaAccount({parameterName: accountStripLabel});
        } else {
            const userStripLabel: string | undefined = USER_STRIP_LABELS[propName as keyof typeof USER_STRIP_LABELS];
            if (userStripLabel) {
                searchCriteriaUser({parameterName: userStripLabel});
            }
        }
    }, [activeTab, searchCriteriaAccount, searchCriteriaUser]);

    const onRemovePill = useCallback((propName: keyof SearchCriteria, forRemove?: CriteriaValue) => {
        trackPillRemoving(propName);
        dispatch(removePill({propName, forRemove}));
    }, [trackPillRemoving, dispatch]);

    const clearCriteria = useCallback(() => {
        dispatch(clearStrip());
        if (activeTab === 'accounts') {
            searchCriteriaAccount({parameterName: 'Clear all criteria'});
        } else {
            searchCriteriaUser({parameterName: 'Clear all criteria'});
        }
    }, [activeTab, dispatch, searchCriteriaAccount, searchCriteriaUser]);

    const stripView = useMemo(() => stripData?.map(({propName, pill, label}) => (
        <StripOfPills key={propName} label={label}>
            {Array.isArray(pill) ? pill.map(({visibleValue, value}) => (
                <Pill
                    className={criteriaStripPill}
                    key={visibleValue}
                    name={visibleValue}
                    removable={showClear ? true : (stripData.length > 1 || pill.length > 1)}
                    preventedRemoveHandler={() => onRemovePill(propName, value)}
                />
            )) : ([
                <Pill
                    className={criteriaStripPill}
                    key={propName}
                    name={pill.visibleValue}
                    removable={showClear ? true : stripData.length > 1}
                    preventedRemoveHandler={() => onRemovePill(propName)}
                />,
            ])}
        </StripOfPills>
    )), [stripData, showClear, onRemovePill]);

    return (
        <span className={criteriaStripGroup}>
            <Scrollable>
                {stripView}
                {showClear && !!strip?.length ? (
                    <FlatButton
                        className={criteriaStripClearButton}
                        startIcon={<Icon type={Icons.TRASH}/>}
                        onClick={clearCriteria}>
                        Clear all criteria
                    </FlatButton>
                ) : null}
            </Scrollable>
        </span>
    );
};
